import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, CheckCircle2, X, XCircle } from "lucide-react";
import { type QuizQuestion } from "~/api/types";
import { Alert, AlertDescription, AlertTitle } from "~/atoms/alert";
import { Button } from "~/atoms/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "~/atoms/card";
import { Item, ItemContent, ItemMedia, ItemTitle } from "~/atoms/item";
import { Progress } from "~/atoms/progress";
import { cn } from "~/lib/utils";

interface Props {
  questions: QuizQuestion[];
  onDone: () => void;
}

/**
 * Take a quiz one question at a time. A wrong pick can be retried; the correct
 * pick locks the question and shows the reason. Owns no data fetching or layout
 * chrome, so it can move into a dialog or page later.
 */
export const QuizTaker = ({ questions, onDone }: Props) => {
  const { t } = useTranslation();
  const [questionIndex, setQuestionIndex] = useState(0);
  const [pickedOptionIndexes, setPickedOptionIndexes] = useState<number[]>([]);

  const question = questions[questionIndex];
  if (!question) return null;

  const total = questions.length;
  const isLast = questionIndex === total - 1;
  const isAnswered = question.options.some(
    (option, index) => option.isCorrect && pickedOptionIndexes.includes(index),
  );
  const hasWrongPick = !isAnswered && pickedOptionIndexes.length > 0;
  const progressPercent =
    ((questionIndex + (isAnswered ? 1 : 0)) / total) * 100;

  const goToNextQuestion = () => {
    setQuestionIndex(questionIndex + 1);
    setPickedOptionIndexes([]);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <p className="text-muted-foreground">
          {t(($) => $.quiz.progress, {
            current: questionIndex + 1,
            total,
          })}
        </p>
        <Progress value={progressPercent} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{question.question}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            {question.options.map((option, index) => {
              const isWrong =
                pickedOptionIndexes.includes(index) && !option.isCorrect;
              const isCorrect = isAnswered && option.isCorrect;
              return (
                <Item
                  key={index}
                  variant="outline"
                  asChild
                  className={cn(
                    "enabled:hover:bg-muted cursor-pointer text-left disabled:cursor-not-allowed",
                    isCorrect && "border-success bg-success/10 text-success",
                    isWrong &&
                      "border-destructive bg-destructive/10 text-destructive",
                  )}
                >
                  <button
                    type="button"
                    onClick={() =>
                      setPickedOptionIndexes([...pickedOptionIndexes, index])
                    }
                    disabled={isAnswered || isWrong}
                  >
                    <ItemContent>
                      <ItemTitle className="line-clamp-none">
                        {option.text}
                      </ItemTitle>
                    </ItemContent>
                    {(isCorrect || isWrong) && (
                      <ItemMedia variant="icon">
                        {isCorrect ? <Check /> : <X />}
                      </ItemMedia>
                    )}
                  </button>
                </Item>
              );
            })}
          </div>

          {isAnswered && (
            <Alert variant="success">
              <CheckCircle2 />
              <AlertTitle>{t(($) => $.quiz.reasonHeading)}</AlertTitle>
              <AlertDescription>{question.reason}</AlertDescription>
            </Alert>
          )}

          {hasWrongPick && (
            <Alert variant="destructive">
              <XCircle />
              <AlertTitle>{t(($) => $.quiz.incorrect)}</AlertTitle>
            </Alert>
          )}
        </CardContent>
        <CardFooter>
          <Button
            onClick={isLast ? onDone : goToNextQuestion}
            disabled={!isAnswered}
          >
            {isLast
              ? t(($) => $.commons.buttons.finish)
              : t(($) => $.quiz.next)}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};
